import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, parseISO, isPast, isToday } from 'date-fns';
import {
  Plus,
  Trash2,
  Edit2,
  CalendarIcon,
  CheckCircle2,
  Circle,
  Clock,
  Flag,
  X,
  LayoutTemplate,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { ApplyTemplateDialog, SaveAsTemplateDialog } from './MilestoneTemplateDialogs';

/**
 * MilestonesDialog
 * Vertical timeline of milestones for a single renovation project.
 * The parent passes {open, onOpenChange, project, onProjectChange}.
 * After every mutation, we re-fetch the project and hand it back up so the
 * parent card's progress bar / count stay in sync.
 */
export const MilestonesDialog = ({ open, onOpenChange, project, onProjectChange }) => {
  const [milestones, setMilestones] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: '', target_date: null });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!project?.id) return;
    try {
      const { data } = await api.get(`/renovation-projects/${project.id}`);
      const sorted = [...(data.milestones || [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      );
      setMilestones(sorted);
      onProjectChange?.(data);
    } catch (_) {
      // ignore
    }
  }, [project?.id, onProjectChange]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    if (!newTitle.trim()) {
      toast.error('Milestone title required');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/renovation-projects/${project.id}/milestones`, {
        title: newTitle.trim(),
        target_date: newDate ? format(newDate, 'yyyy-MM-dd') : null,
      });
      setNewTitle('');
      setNewDate(null);
      await refresh();
      toast.success('Milestone added');
    } catch (_) {
      toast.error('Failed to add milestone');
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (m) => {
    const nextStatus = m.status === 'completed' ? 'pending' : 'completed';
    try {
      await api.put(
        `/renovation-projects/${project.id}/milestones/${m.id}`,
        { status: nextStatus }
      );
      await refresh();
    } catch (_) {
      toast.error('Failed to update milestone');
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditDraft({
      title: m.title,
      target_date: m.target_date ? parseISO(m.target_date) : null,
    });
  };

  const saveEdit = async (m) => {
    if (!editDraft.title.trim()) {
      toast.error('Title required');
      return;
    }
    try {
      await api.put(
        `/renovation-projects/${project.id}/milestones/${m.id}`,
        {
          title: editDraft.title.trim(),
          target_date: editDraft.target_date
            ? format(editDraft.target_date, 'yyyy-MM-dd')
            : null,
        }
      );
      setEditingId(null);
      await refresh();
    } catch (_) {
      toast.error('Failed to save milestone');
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete milestone "${m.title}"?`)) return;
    try {
      await api.delete(
        `/renovation-projects/${project.id}/milestones/${m.id}`
      );
      await refresh();
      toast.success('Milestone deleted');
    } catch (_) {
      toast.error('Failed to delete');
    }
  };

  const done = milestones.filter((m) => m.status === 'completed').length;
  const total = milestones.length;
  const overdue = (m) =>
    m.status !== 'completed' &&
    m.target_date &&
    isPast(parseISO(m.target_date)) &&
    !isToday(parseISO(m.target_date));

  const [applyOpen, setApplyOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto"
        data-testid="milestones-dialog"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="font-manrope flex items-center gap-2">
                <Flag className="h-5 w-5 text-slate-700" />
                Milestones
              </DialogTitle>
              <p className="text-sm text-slate-500 mt-1">
                {project?.title} · {done} of {total} complete
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setApplyOpen(true)}
                data-testid="open-apply-template-btn"
                title="Apply a saved template"
              >
                <LayoutTemplate className="h-3.5 w-3.5 mr-1" />
                Templates
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={total === 0}
                onClick={() => setSaveOpen(true)}
                data-testid="open-save-template-btn"
                title="Save current milestones as a template"
              >
                <Layers className="h-3.5 w-3.5 mr-1" />
                Save
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Timeline */}
        <div className="mt-3">
          {total === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">
              No milestones yet. Add your first one below.
            </div>
          ) : (
            <ol className="relative pl-8">
              {/* vertical line */}
              <span
                className="absolute left-3 top-2 bottom-2 w-0.5 bg-slate-200"
                aria-hidden
              />

              {milestones.map((m) => {
                const isDone = m.status === 'completed';
                const isEditing = editingId === m.id;
                const isOverdue = overdue(m);

                return (
                  <li
                    key={m.id}
                    className="relative pb-6 last:pb-0"
                    data-testid={`milestone-item-${m.id}`}
                  >
                    {/* Node */}
                    <button
                      type="button"
                      onClick={() => handleToggle(m)}
                      className="absolute -left-8 top-0.5 h-6 w-6 rounded-full bg-white border-2 flex items-center justify-center transition-colors"
                      style={{
                        borderColor: isDone
                          ? '#059669'
                          : isOverdue
                          ? '#dc2626'
                          : '#94a3b8',
                      }}
                      data-testid={`milestone-toggle-${m.id}`}
                      title={isDone ? 'Mark as pending' : 'Mark as complete'}
                    >
                      {isDone ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <Circle
                          className={cn(
                            'h-3 w-3',
                            isOverdue ? 'text-red-500' : 'text-slate-400'
                          )}
                        />
                      )}
                    </button>

                    {/* Content */}
                    {isEditing ? (
                      <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-2">
                        <Input
                          value={editDraft.title}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, title: e.target.value }))
                          }
                          className="text-sm"
                          data-testid={`milestone-edit-title-${m.id}`}
                        />
                        <div className="flex items-center gap-2">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={cn(
                                  'text-xs',
                                  !editDraft.target_date && 'text-slate-400'
                                )}
                              >
                                <CalendarIcon className="h-3 w-3 mr-1" />
                                {editDraft.target_date
                                  ? format(editDraft.target_date, 'MMM d, yyyy')
                                  : 'Target date'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={editDraft.target_date}
                                onSelect={(d) =>
                                  setEditDraft((prev) => ({ ...prev, target_date: d }))
                                }
                              />
                            </PopoverContent>
                          </Popover>
                          <div className="flex-1" />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingId(null)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            className="bg-slate-900 hover:bg-slate-800 h-8 text-xs"
                            onClick={() => saveEdit(m)}
                            data-testid={`milestone-save-${m.id}`}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p
                            className={cn(
                              'font-medium text-slate-900 truncate',
                              isDone && 'line-through text-slate-400'
                            )}
                          >
                            {m.title}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 text-xs">
                            {m.target_date && (
                              <span
                                className={cn(
                                  'inline-flex items-center gap-1',
                                  isOverdue
                                    ? 'text-red-600 font-medium'
                                    : 'text-slate-500'
                                )}
                              >
                                <CalendarIcon className="h-3 w-3" />
                                {format(parseISO(m.target_date), 'MMM d, yyyy')}
                                {isOverdue && ' · overdue'}
                              </span>
                            )}
                            {isDone && m.completed_date && (
                              <span className="inline-flex items-center gap-1 text-emerald-600">
                                <Clock className="h-3 w-3" />
                                done {format(parseISO(m.completed_date), 'MMM d')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(m)}
                            className="h-7 w-7 p-0"
                            data-testid={`milestone-edit-${m.id}`}
                          >
                            <Edit2 className="h-3 w-3 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(m)}
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`milestone-delete-${m.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Add row */}
        <form
          onSubmit={handleAdd}
          className="mt-2 border-t border-slate-100 pt-4 space-y-2"
        >
          <Label className="text-xs uppercase tracking-wide text-slate-500">
            Add milestone
          </Label>
          <div className="flex items-center gap-2">
            <Input
              placeholder="e.g. Cabinets installed"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              data-testid="milestone-new-title"
              className="flex-1"
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    'shrink-0',
                    !newDate && 'text-slate-400'
                  )}
                  data-testid="milestone-new-date-btn"
                >
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  {newDate ? format(newDate, 'MMM d') : 'Target'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={newDate}
                  onSelect={setNewDate}
                />
              </PopoverContent>
            </Popover>
            <Button
              type="submit"
              disabled={busy}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="milestone-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </form>
      </DialogContent>

      <ApplyTemplateDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        project={project}
        onApplied={(updated) => {
          onProjectChange?.(updated);
          refresh();
        }}
      />
      <SaveAsTemplateDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        project={project}
      />
    </Dialog>
  );
};

export default MilestonesDialog;

